/**
 * Unified Orders / Receptions list table.
 *
 * Shows a single row per item from `unifiedList`, with status dropdowns for orders
 * and a single "Réceptionnée" badge for legacy manual receptions.
 *
 * Extracted from OrdersTab.jsx (≈160 lines) — pure presentational, all state
 * lives in the parent.
 */
import React, { useState, useEffect } from 'react';
import {
    ShoppingCart,
    Eye,
    ChevronDown,
    Send,
    PackageCheck,
    PackageX,
    Trash2,
    FileText,
    FileScan,
    ArrowLeftRight,
} from 'lucide-react';
import { formatCurrency, formatDate, getStatusLabel, getStatusClass } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

export default function OrdersListTable({
    unifiedList,
    calculateLineTotal,
    onOpenOrderDetail,
    onOpenReception,
    onInvoiceReception,
    onSendEmail,
    onDeleteOrder,
    onViewManualReception,
    onDeleteManualReception,
    onViewTransfer,
    onStatusChange,
    onCloseRemaining,
    canSendEmail,
    canDelete,
    canCreateReception,
}) {
    const PAGE_SIZE = (typeof window !== 'undefined' && window.innerWidth < 640) ? 30 : 100;
    const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
    useEffect(() => { setVisibleCount(PAGE_SIZE); }, [unifiedList.length, PAGE_SIZE]);
    const visibleItems = unifiedList.slice(0, visibleCount);
    const remaining = unifiedList.length - visibleItems.length;

    const computeAmount = (item) => {
        if (!item._isFromOrders) return item.total_amount;
        const lines = item.items || [];
        const hasReception = (item.status === 'delivered' || item.status === 'partially_received')
            && lines.some(i => (i.quantity_received || 0) > 0);
        return lines.reduce((sum, i) => sum + calculateLineTotal(
            hasReception ? { ...i, quantity: i.quantity_received || 0 } : i
        ), 0);
    };

    const typeBadge = (item) => {
        if (item.email_status === 'failed') return <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200" title={item.email_error || ''}><Send className="w-3 h-3 mr-1" />Échec email</Badge>;
        if (item.email_status === 'retrying') return <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200" title={item.email_error || ''}><Send className="w-3 h-3 mr-1" />Renvoi en cours…</Badge>;
        if (item.email_status === 'sent_after_retry') return <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200" title={`Envoyé après ${item.email_attempts || 2} tentatives automatiques`}><Send className="w-3 h-3 mr-1" />Envoyé après relance</Badge>;
        if (item._type === 'draft') return <Badge variant="outline" className="bg-slate-50 text-slate-600 border-slate-300"><FileText className="w-3 h-3 mr-1" />Saisie</Badge>;
        if (item._type === 'ordered') return <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200"><Send className="w-3 h-3 mr-1" />Commandé</Badge>;
        if (item._type === 'transfer_in') return <Badge variant="outline" className="bg-violet-50 text-violet-700 border-violet-200"><ArrowLeftRight className="w-3 h-3 mr-1" />Transfert</Badge>;
        return <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200"><PackageCheck className="w-3 h-3 mr-1" />Réception</Badge>;
    };

    return (
        <Card>
            <CardContent className="p-0">
                {unifiedList.length === 0 ? (
                    <div className="py-12 text-center">
                        <ShoppingCart className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                        <p className="text-slate-500">Aucune commande ou réception trouvée</p>
                    </div>
                ) : (
                    <>
                    {/* Vue mobile compacte (cartes) — évite le gel du scroll Android */}
                    <div className="sm:hidden divide-y divide-slate-100">
                        {visibleItems.map((item) => (
                            <div
                                key={`m-${item._type}-${item.id}`}
                                className="px-3 py-2.5 active:bg-slate-50"
                                data-testid={`m-${item._type}-row-${item.id}`}
                                onClick={() => {
                                    if (item._isFromOrders) onOpenOrderDetail(item);
                                    else if (item._type === 'transfer_in') onViewTransfer(item);
                                    else onViewManualReception(item);
                                }}
                            >
                                <div className="flex items-center justify-between gap-2">
                                    <span className="font-mono font-medium text-[11px] text-slate-700">{item._number}</span>
                                    <span className="font-semibold text-sm">{formatCurrency(computeAmount(item))}</span>
                                </div>
                                <div className="flex items-center justify-between gap-2 mt-1">
                                    <span className="text-xs text-slate-600 truncate">
                                        {item.supplier_name} · {item.site_name}
                                    </span>
                                    <span className="text-[11px] text-slate-400 whitespace-nowrap">{formatDate(item._date)}</span>
                                </div>
                                <div className="flex items-center justify-between gap-2 mt-1.5">
                                    {typeBadge(item)}
                                    <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                                        {item._isFromOrders ? (
                                            <>
                                                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium ${getStatusClass(item.status)}`}>
                                                    {getStatusLabel(item.status)}
                                                </span>
                                                {canCreateReception && item.status !== 'delivered' && item.status !== 'cancelled' && (
                                                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-emerald-500" onClick={() => onOpenReception(item)} data-testid={`m-receive-order-${item.id}`}>
                                                        <PackageCheck className="w-4 h-4" />
                                                    </Button>
                                                )}
                                                {canCreateReception && item.status !== 'delivered' && item.status !== 'cancelled' && onInvoiceReception && (
                                                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-orange-500" onClick={() => onInvoiceReception(item)} data-testid={`m-invoice-receive-order-${item.id}`}>
                                                        <FileScan className="w-4 h-4" />
                                                    </Button>
                                                )}
                                                <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => onOpenOrderDetail(item)} data-testid={`m-view-order-${item.id}`}>
                                                    <Eye className="w-4 h-4" />
                                                </Button>
                                            </>
                                        ) : item._type === 'transfer_in' ? (
                                            <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-slate-500" onClick={() => onViewTransfer(item)} data-testid={`m-view-transfer-${item.id}`}>
                                                <Eye className="w-4 h-4" />
                                            </Button>
                                        ) : (
                                            <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-slate-500" onClick={() => onViewManualReception(item)} data-testid={`m-view-reception-${item.id}`}>
                                                <Eye className="w-4 h-4" />
                                            </Button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className="hidden sm:block overflow-x-auto">
                        <table className="w-full data-table">
                            <thead>
                                <tr className="table-header">
                                    <th className="text-left px-3 py-1">N°</th>
                                    <th className="text-left px-3 py-1">Type</th>
                                    <th className="text-left px-3 py-1">Date</th>
                                    <th className="text-left px-3 py-1">Fournisseur</th>
                                    <th className="text-left px-3 py-1">Site</th>
                                    <th className="text-left px-2 py-1 w-[90px]">N° Pièce</th>
                                    <th className="text-right px-3 py-1">Montant</th>
                                    <th className="text-center px-3 py-1">Statut</th>
                                    <th className="text-right px-1 py-1 sticky right-0 bg-slate-50 z-10">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {visibleItems.map((item) => (
                                    <tr key={`${item._type}-${item.id}`} className="table-row" data-testid={`${item._type}-row-${item.id}`}>
                                        <td className="px-3 py-1 font-mono font-medium text-[11px] text-slate-700">{item._number}</td>
                                        <td className="px-3 py-1">
                                            {item._type === 'draft' ? (
                                                <Badge variant="outline" className="bg-slate-50 text-slate-600 border-slate-300">
                                                    <FileText className="w-3 h-3 mr-1" />
                                                    Saisie
                                                </Badge>
                                            ) : item._type === 'ordered' ? (
                                                <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                                                    <Send className="w-3 h-3 mr-1" />
                                                    Commandé
                                                </Badge>
                                            ) : item._type === 'transfer_in' ? (
                                                <Badge variant="outline" className="bg-violet-50 text-violet-700 border-violet-200">
                                                    <ArrowLeftRight className="w-3 h-3 mr-1" />
                                                    Transfert
                                                </Badge>
                                            ) : (
                                                <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">
                                                    <PackageCheck className="w-3 h-3 mr-1" />
                                                    Réception
                                                </Badge>
                                            )}
                                        </td>
                                        <td className="px-3 py-1 text-slate-600">{formatDate(item._date)}</td>
                                        <td className="px-3 py-1">{item.supplier_name}</td>
                                        <td className="px-3 py-1">
                                            {item._type === 'transfer_in' ? (
                                                <span>
                                                    {item.site_name}
                                                    <span className="text-violet-500 text-[11px] ml-1.5" title={`Transfert depuis ${item.from_site_name}`}>← {item.from_site_name}</span>
                                                </span>
                                            ) : item.site_name}
                                        </td>
                                        <td className="px-2 py-1 font-mono text-[11px] text-slate-600 max-w-[90px]" data-testid={`${item._type}-doc-${item.id}`}>
                                            {(() => {
                                                const showDoc = item._isFromOrders
                                                    ? (item.status === 'delivered' || item.status === 'partially_received')
                                                    : true;
                                                if (!showDoc) return <span className="text-slate-300">—</span>;
                                                const doc = item.document_number || item.invoice_number;
                                                return doc
                                                    ? <span className="block truncate" title={doc}>{doc}</span>
                                                    : <span className="text-slate-300 italic">(aucun)</span>;
                                            })()}
                                        </td>
                                        <td className="px-3 py-1 text-right font-semibold">{formatCurrency(computeAmount(item))}</td>
                                        <td className="px-3 py-1 text-center">
                                            {item._isFromOrders ? (
                                                <DropdownMenu>
                                                    <DropdownMenuTrigger asChild>
                                                        <button className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium ${getStatusClass(item.status)}`}>
                                                            {getStatusLabel(item.status)}
                                                            <ChevronDown className="w-3 h-3" />
                                                        </button>
                                                    </DropdownMenuTrigger>
                                                    <DropdownMenuContent>
                                                        <DropdownMenuItem onClick={() => onStatusChange(item.id, 'pending')}>En attente</DropdownMenuItem>
                                                        <DropdownMenuItem onClick={() => onStatusChange(item.id, 'confirmed')}>Confirmée</DropdownMenuItem>
                                                        <DropdownMenuItem onClick={() => onStatusChange(item.id, 'shipped')}>Expédiée</DropdownMenuItem>
                                                        <DropdownMenuItem onClick={() => onStatusChange(item.id, 'partially_received')}>Partiellement reçue</DropdownMenuItem>
                                                        <DropdownMenuItem onClick={() => onStatusChange(item.id, 'delivered')}>Livrée</DropdownMenuItem>
                                                        <DropdownMenuItem onClick={() => onStatusChange(item.id, 'cancelled')}>Annulée</DropdownMenuItem>
                                                    </DropdownMenuContent>
                                                </DropdownMenu>
                                            ) : item._type === 'transfer_in' ? (
                                                <Badge className="bg-violet-100 text-violet-700 hover:bg-violet-100">
                                                    Transférée
                                                </Badge>
                                            ) : (
                                                <Badge className="bg-emerald-100 text-emerald-700">
                                                    Réceptionnée
                                                </Badge>
                                            )}
                                        </td>
                                        <td className="px-1 py-1 text-right whitespace-nowrap sticky right-0 bg-white z-10 shadow-[-4px_0_6px_-4px_rgba(0,0,0,0.08)]">
                                            {item._isFromOrders ? (
                                                <>
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() => onOpenOrderDetail(item)}
                                                        data-testid={`view-order-${item.id}`}
                                                        title="Voir détails"
                                                    >
                                                        <Eye className="w-4 h-4" />
                                                    </Button>
                                                    {canCreateReception && item.status !== 'delivered' && item.status !== 'cancelled' && (
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            className="text-emerald-500 hover:text-emerald-700 hover:bg-emerald-50"
                                                            onClick={() => onOpenReception(item)}
                                                            data-testid={`receive-order-${item.id}`}
                                                            title="Réceptionner"
                                                        >
                                                            <PackageCheck className="w-4 h-4" />
                                                        </Button>
                                                    )}
                                                    {canCreateReception && item.status !== 'delivered' && item.status !== 'cancelled' && onInvoiceReception && (
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            className="text-orange-500 hover:text-orange-700 hover:bg-orange-50"
                                                            onClick={() => onInvoiceReception(item)}
                                                            data-testid={`invoice-receive-order-${item.id}`}
                                                            title="Réceptionner via facture (IA)"
                                                        >
                                                            <FileScan className="w-4 h-4" />
                                                        </Button>
                                                    )}
                                                    {canCreateReception && item.status === 'partially_received' && onCloseRemaining && (
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            className="text-amber-600 hover:text-amber-800 hover:bg-amber-50"
                                                            onClick={() => onCloseRemaining(item)}
                                                            data-testid={`close-remaining-${item.id}`}
                                                            title="Clôturer les quantités restantes"
                                                        >
                                                            <PackageX className="w-4 h-4" />
                                                        </Button>
                                                    )}
                                                    {canSendEmail && item.status === 'pending' && !item.email_sent_at && (
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            className="text-blue-500 hover:text-blue-700 hover:bg-blue-50"
                                                            onClick={() => onSendEmail(item)}
                                                            data-testid={`send-email-order-${item.id}`}
                                                            title="Envoyer par email"
                                                        >
                                                            <Send className="w-4 h-4" />
                                                        </Button>
                                                    )}
                                                    {canDelete && (
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        className="text-red-500 hover:text-red-700 hover:bg-red-50"
                                                        onClick={() => onDeleteOrder(item)}
                                                        data-testid={`delete-order-${item.id}`}
                                                        title="Supprimer"
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </Button>
                                                    )}
                                                </>
                                            ) : item._type === 'transfer_in' ? (
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    className="text-slate-500 hover:text-slate-700"
                                                    onClick={() => onViewTransfer(item)}
                                                    data-testid={`view-transfer-in-${item.id}`}
                                                    title="Voir le transfert reçu"
                                                >
                                                    <Eye className="w-4 h-4" />
                                                </Button>
                                            ) : (
                                                <>
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        className="text-slate-500 hover:text-slate-700"
                                                        onClick={() => onViewManualReception(item)}
                                                        data-testid={`view-manual-reception-${item.id}`}
                                                        title="Voir détails"
                                                    >
                                                        <Eye className="w-4 h-4" />
                                                    </Button>
                                                    {canDelete && (
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        className="text-red-500 hover:text-red-700 hover:bg-red-50"
                                                        onClick={() => onDeleteManualReception(item)}
                                                        data-testid={`delete-manual-reception-${item.id}`}
                                                        title="Supprimer"
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </Button>
                                                    )}
                                                </>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    {remaining > 0 && (
                        <div className="py-3 text-center border-t">
                            <button
                                type="button"
                                onClick={() => setVisibleCount(c => c + PAGE_SIZE)}
                                className="px-4 py-1.5 rounded-full text-xs font-semibold bg-white text-slate-700 border border-slate-300 hover:border-slate-500 transition-colors"
                                data-testid="orders-show-more-btn"
                            >
                                Afficher plus ({remaining} restantes)
                            </button>
                        </div>
                    )}
                    </>
                )}
            </CardContent>
        </Card>
    );
}
